import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeForUser } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { NauticusLogo } from "@/components/NauticusLogo";
import { SBALogo } from "@/components/SBALogo";
import { PoweredByFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1549890762-75fd80457275?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxsaWdodGhvdXNlJTIwc3Vuc2V0fGVufDB8fHx8MTc4ODE0NTAyNXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const isSBA = new URLSearchParams(window.location.search).get("brand") === "sba";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const rawNext = params.get("next");
      // Only allow safe internal destinations (reject external / protocol-relative / scheme URLs).
      const next = (rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//")
        && !rawNext.startsWith("/\\") && !rawNext.includes("://")) ? rawNext : null;
      const resume = params.get("resume");
      let askToken = null;
      try {
        const t = localStorage.getItem("ask_session_token");
        askToken = t && localStorage.getItem("ask_save_intent") === t ? t : null;
      } catch { /* ignore */ }
      const u = await login(email, password, {
        resume_token: resume || undefined,
        ask_session_token: askToken || undefined,
      });
      let toHousehold = false;
      try { toHousehold = !!askToken && sessionStorage.getItem("ask_after_save") === "household"; sessionStorage.removeItem("ask_after_save"); } catch { /* ignore */ }
      navigate(toHousehold ? "/app/member#my-household" : (next || homeForUser(u)));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Lighthouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 nauticus-hero-overlay" />
        <div className="relative h-full flex flex-col justify-between p-12">
          {isSBA ? (
            <div className="flex flex-col leading-none" data-testid="login-sba-lockup">
              <SBALogo light className="h-9 sm:h-11" />
              <span data-testid="login-sba-tagline" className="mt-2 pl-[2px] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-[#EDE7D6]">
                Senior Intelligence. Better Decisions.
              </span>
            </div>
          ) : (
            <NauticusLogo light />
          )}
          <div>
            <h2 className="font-display text-4xl font-bold text-white tracking-tight leading-tight max-w-md">
              Welcome back. My Nauticus is waiting.
            </h2>
            <p className="text-mist-200 mt-4 max-w-md">
              Pick up right where you left off — your benefits, your family, your pace.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-mist-100">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            {isSBA ? (
              <div className="flex flex-col leading-none" data-testid="login-sba-lockup-mobile">
                <SBALogo className="h-8 sm:h-10" />
                <span data-testid="login-sba-tagline-mobile" className="mt-2 pl-[2px] text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1E3E62]">
                  Senior Intelligence. Better Decisions.
                </span>
              </div>
            ) : (
              <NauticusLogo />
            )}
          </div>
          <h1 className="font-display text-3xl font-bold text-navy-900 tracking-tight">Sign in</h1>
          <p className="text-muted-foreground mt-2 mb-8">{isSBA ? "Access your Senior Benefit Advisors account." : "Access your Nauticus benefits dashboard."}</p>

          {error && (
            <Alert variant="destructive" className="mb-5" data-testid="login-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="mt-1.5 h-11 bg-white" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-semibold">Password</Label>
                <Link to={isSBA ? "/forgot-password?brand=sba" : "/forgot-password"}
                  className="text-xs text-gold-dark font-semibold hover:underline" data-testid="login-forgot-password">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5 h-11 bg-white" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-navy-800 hover:bg-navy-700 text-white rounded-full font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            New here?{" "}
            <Link to={(() => { const n = new URLSearchParams(window.location.search).get("next"); return n && n.startsWith("/compare/") ? `/register?next=${encodeURIComponent(n)}` : (isSBA ? "/register?brand=sba" : "/register"); })()} className="text-gold-dark font-semibold hover:underline" data-testid="go-register">
              Create your account
            </Link>
          </p>
          <PoweredByFooter className="mt-10" />
        </div>
      </div>
    </div>
  );
}
